// Lightweight list of still-active Waitlist provider requests.

import type { NavigatorProviderRequestStatus } from "../types";
import DetailItem from "./DetailItem";

type ActiveWaitlistsCardProps = {
  waitlists: NavigatorProviderRequestStatus[];
  outstandingPatientContactRequestIds?: number[];
  onViewProviderProfile?: (familyDoctorId: number) => void;
};

export default function ActiveWaitlistsCard({
  waitlists,
  outstandingPatientContactRequestIds = [],
  onViewProviderProfile,
}: ActiveWaitlistsCardProps) {
  if (!Array.isArray(waitlists) || waitlists.length === 0) {
    return null;
  }

  const outstandingIds = new Set(
    (outstandingPatientContactRequestIds || [])
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0)
  );

  return (
    <section className="cn-case-detail-card cn-case-detail-wide cn-active-waitlists">
      <div className="cn-case-detail-card-header">
        <h2>Active waitlists</h2>
      </div>
      <p className="cn-active-waitlists-note">
        The patient remains on these waitlists while other providers are
        contacted.
      </p>

      <ul className="cn-active-waitlists-list">
        {waitlists.map((request) => {
          const doctorId = Number(request.familyDoctorProfileId || 0);
          const canView =
            typeof onViewProviderProfile === "function" &&
            Number.isFinite(doctorId) &&
            doctorId > 0;
          const needsPatientUpdate = outstandingIds.has(
            Number(request.requestId)
          );

          return (
            <li key={request.requestId}>
              <dl className="cn-case-detail-list cn-case-detail-list-three">
                <DetailItem
                  label="Provider"
                  value={request.familyDoctorName || "—"}
                />
                <DetailItem label="Clinic" value={request.clinicName || "—"} />
                <DetailItem
                  label="Status"
                  value={request.statusLabel || "Waitlist"}
                />
              </dl>
              {needsPatientUpdate ? (
                <p className="cn-active-waitlists-patient-update">
                  Patient update required
                </p>
              ) : null}
              {canView ? (
                <div className="cn-follow-up-outcome-action">
                  <button
                    type="button"
                    className="cn-follow-up-outcome-secondary"
                    onClick={() => onViewProviderProfile?.(doctorId)}
                  >
                    View provider profile
                  </button>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
